/* eslint-disable max-len,@typescript-eslint/prefer-optional-chain */
import * as mobx from 'mobx';
import { transaction } from '@src/slide/domain/slide-app';
import Modal from '@tencent/dui/lib/components/Modal';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import BaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/base-model';
import GroupModel from '@src/slide/domain/ui-model/element-modeles/group-model';
import TableModel from '@src/slide/domain/ui-model/element-modeles/table-model';
import ShapeModel from '@src/slide/domain/ui-model/element-modeles/shape-model';
import i18n from '@i18next/i18n';
import { INoteModel } from '@src/slide/domain/ui-model/element-modeles/Interface/note-model.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import SearchReplaceDialogController from './search-replace-dialog-controller';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { isSdkModelIsAttachment } from '@src/slide/domain/ui-model/elements/element-judge';
import {
    DataType, SearchResult, INoteSlideDataModel, IPageDataModel, ShapeDataModel, TableDataModel, MutationSource,
} from '@tencent/slide-sdk-model';
import {
    ISearchReplaceManager, ISearchReplaceManagerServices, Position, Result, SearchOrder, SearchReplaceType,
} from './search-replace-manager-v3.interface';
import { IDrawingShapeDataModel } from '@tencent/slide-sdk-model/src/model/group-shape-data-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IToolbarStateManager } from '../toolbar-state-manager/core/toolbar-state-manager.interface';

const MAX_KEYWORD = 500;


export default class SearchReplaceManager implements ISearchReplaceManager {
    @mobx.observable
    public currentIndex = 0; // 当前高亮搜索结果index
    @mobx.observable
    public dialogOpenFlag = false; // dialog打开状态
    @mobx.observable
    public isHighlighting = false; // 当前是否有高亮
    @mobx.observable
    public isSearchingFlag = false; // 是否搜索状态
    @mobx.observable
    public keywordCount = 0; // 搜索结果数量
    @mobx.observable
    public searchReplaceType: SearchReplaceType = SearchReplaceType.Search; // 当前类型
    public changePageFlag = false; // 切页逻辑，表示主动切页
    public currentPageResult: Array<Result>; // 当前页搜索结果
    public keyword: string; // 搜索关键词
    public lastHighlightPosition: Position;
    public modal = false; // 弹出框状态
    public rawSearchResult: SearchResult;
    public searchResult: Array<Result>; // 全量搜索结果
    private userPriviledgeService: IUserPriviledgeService;
    private masterLayoutEditService: IMasterLayoutEditService;
    private uiDataService: IUiDataService;
    private editorInstanceService: IEditorInstanceService;
    private selectionService: ISelectionService;
    private toolbarStateManager: IToolbarStateManager;
    private inlineNodeKeywordCount = 0; // inlineNode内的搜索结果数量

    public constructor() {
        this.isSearchingFlag = false;

        SlideDataServices.SDKDataServices.EventManager.promise().then(({ mutationEvents }) => {
            // 协同 提示重新搜索
            mutationEvents.onCommitCommand((source: MutationSource) => {
                if (source === MutationSource.UndoRedo) {
                    this.exitSearching();
                    return;
                }
                if (source === MutationSource.Collaborator) {
                    this.handleChangeset();
                }
            });
        });

        window.addEventListener('enterEditing', this.exitSearching);
    }

    public setServiceDelegates(services: ISearchReplaceManagerServices): void {
        const {
            userPriviledgeService,
            uiDataService,
            editorInstanceService,
            selectionService,
            masterLayoutEditService,
            toolbarStateManager,
        } = services;
        this.userPriviledgeService = userPriviledgeService;
        this.uiDataService = uiDataService;
        this.editorInstanceService = editorInstanceService;
        this.selectionService = selectionService;
        this.masterLayoutEditService = masterLayoutEditService;
        this.toolbarStateManager = toolbarStateManager;
    }

    public init() {
        new SearchReplaceDialogController().init({
            selectionService: this.selectionService,
            userPriviledgeService: this.userPriviledgeService,
            searchReplaceManager: this,
            uiDataService: this.uiDataService,
            masterLayoutEditService: this.masterLayoutEditService,
            toolbarStateManager: this.toolbarStateManager,
        });
    }

    public getUIModelBySDKComponent(
        component: IDrawingShapeDataModel,
        children: Array<IBaseModel> = Array.from(this.selectionService
            .currentSlide?.children || []),
        note: INoteModel|undefined = this.selectionService.currentSlide?.noteData,
    ) {
        if (
            note
            && component.getPage().type === DataType.Notes
            && (note?.sdkData as unknown as IPageDataModel) === component.getPage()
        ) {
            return note.noteTextBody;
        }

        for (const model of children) {
            if (model instanceof GroupModel) {
                const m = this.getUIModelBySDKComponent(component, model.children);
                if (m) return m;
            } else {
                if (model.sdkData && (model.sdkData === component)) return model;
            }
        }
    }

    public openDialog(type: SearchReplaceType) {
        if (this.masterLayoutEditService?.isMasterEditType) return;
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_SEARCH,
            action: type === SearchReplaceType.Search && (TDWActionType.PC_SEARCH_SIDE_BAR || TDWActionType.PC_SEARCH_REP_SIDE_BAR),
        });
        this.editorInstanceService.getEditorInstance()?.triggerBlur();
        this.searchReplaceType = type;
        this.dialogOpenFlag = true;
    }

    public closeDialog() {
        this.dialogOpenFlag = false;
        this.clear();
        this.isSearchingFlag = false;
    }

    public changeSearchReplaceType(type: SearchReplaceType) {
        this.searchReplaceType = type;
    }

    /**
     * 获取数据
     * 如果有数据，刷新新的ui（不跳转）
     * @param keyword keyword
     */
    public search(keyword: string) {
        mobx.transaction(() => {
            this.isHighlighting = false;

            if (this.uiDataService.presentation.operation.loading) {
                DUISnackbar.show({
                    message: i18n.t('正在加载幻灯片，请稍后再试'),
                    type: 'info',
                    autoClose: true,
                });
                return false;
            }

            // 清除页面选中态
            this.selectionService.selectedSlides.clear();

            // 如果在搜索状态，且keyword没有改变，不需要重新发起请求
            if (keyword === this.keyword && this.isSearchingFlag) {
                return false;
            }
            this.isSearchingFlag = true;

            if (this.currentPageResult) {
                this.unMarkResult(this.currentPageResult);
            }
            if (this.lastHighlightPosition) {
                this.cancelHighlight(
                    this.lastHighlightPosition,
                    this.currentPageResult
                        .find(({ component }) => (component === this.lastHighlightPosition.component)),
                );
            }

            this.keyword = keyword;
            this.updateRawSearchResult(this.keyword);
            this.updateSearchResult(this.rawSearchResult);
            if (!(this.searchResult && this.searchResult.length)) {
                this.currentPageResult = [];
                return true;
            }

            this.currentPageResult = this.getIndexPageResult(
                this.searchResult,
                this.selectionService.currentSlideIndex,
            );
            if (this.currentPageResult.length) {
                this.updateSearchResultUI(this.currentPageResult);
            }

            return true;
        });
    }

    /**
     * highlight当前的上一个或下一个元素，此时keyword是不会改变的！
     * @param order
     */
    public searchHighlight(order: SearchOrder) {
        let action: string;
        if (this.searchReplaceType === SearchReplaceType.Search) {
            if (order === SearchOrder.Last) {
                action = TDWActionType.PC_SEARCH_LAST;
            } else {
                action = TDWActionType.PC_SEARCH_NEXT;
            }
        } else {
            if (order === SearchOrder.Last) {
                action = TDWActionType.PC_SEARCH_REP_LAST;
            } else {
                action = TDWActionType.PC_SEARCH_REP_NEXT;
            }
        }

        tdw({
            action,
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_SEARCH,
        });

        this.isHighlighting = true;

        // 找到当前页之前之后开始的第一个或者下一个
        if (!this.lastHighlightPosition) {
            this.currentIndex = this.getFirstCurrentIndex(
                this.searchResult,
                this.selectionService.currentSlideIndex,
                order,
            );
        } else {
            this.currentIndex = this.getNextCurrentIndex(this.currentIndex, order, this.keywordCount);
        }

        this.renderHighlight();

        return this.getResultByIndex(this.searchResult, this.currentIndex).position;
    }

    public replace(replaceWord: string) {
        mobx.transaction(() => {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_SEARCH,
                action: TDWActionType.PC_SEARCH_REP_RESULT,
            });

            const position = this.lastHighlightPosition;
            const { component } = position;
            const result: Result | undefined = this.searchResult.find(({ component }) => component === position.component);
            if (!result) {
                return;
            }

            this.unMarkResult(this.currentPageResult).then(() => {
                // 1. 根据当前position找到对应的element
                if (component instanceof TableDataModel) {
                    // table
                    const cellPosition: { row: number; col: number } | undefined = result.result[position.offset].cell;
                    if (!cellPosition) {
                        return;
                    }
                    const textPositions: Array<{ offset: number; paragraph: number }> = [result.result[position.offset]];
                    const tr: any = component.store?.data.graphic?.graphicData?.tbl?.tr;
                    if (!tr) return;
                    let { p } = tr[cellPosition.row].tc[cellPosition.col].txBody;

                    p = this.replacePositionedTextInParagraphList(p, textPositions, this.keyword, replaceWord);
                    component.api.transaction(() => {
                        tr.forEach((tblRow, row) => {
                            tblRow.tc.forEach((tc, col) => {
                                if (row === cellPosition.row && col === cellPosition.col) {
                                    component.api.updateParagraphList(p, col, row);
                                }
                            });
                        });
                    });
                } else if (component instanceof ShapeDataModel) {
                    // shape
                    const textPositions: Array<{ offset: number; paragraph: number }> = [result.result[position.offset]];
                    let paragraphList = this.getParagraph(component);
                    paragraphList = this.replacePositionedTextInParagraphList(paragraphList, textPositions, this.keyword, replaceWord);
                    component.api.updateParagraphList(paragraphList);
                }

                // replaceWord里可能有keyword
                const times = this.getStringTimes(replaceWord, this.keyword);
                if (this.keywordCount && !this.inlineNodeKeywordCount) {
                    // inlineNode内替换不成功，因此 inlineNodeKeywordCount 不为 0 时不减一，保持原数据
                    this.keywordCount = this.keywordCount - 1 + times;
                }
                if (!this.keywordCount) {
                    this.inlineNodeKeywordCount = 0; // 每次替换后清除记录的inlineNode内关键词数量
                    return;
                }
                // 避免inlineNode后插入的文字替换失败
                this.currentIndex += this.inlineNodeKeywordCount;
                this.inlineNodeKeywordCount = 0; // 每次替换后清除记录的inlineNode内关键词数量
                this.currentIndex += times;
                if (this.currentIndex > this.keywordCount - 1) this.currentIndex = 0; // 替换到底，从头开始

                this.nextTick(() => {
                    // 位置很有可能发生改变，为简单起见，重新触发搜索
                    this.isSearchingFlag = false;
                    this.search(this.keyword);
                    if (!this.searchResult.length) return;

                    this.renderHighlight();
                });
            });
        });
    }

    public replaceAll(replaceWord: string) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_SEARCH,
            action: TDWActionType.PC_SEARCH_REP_ALL,
        });

        const count = this.keywordCount;
        const { keyword } = this;
        // 与其他品类策略对齐：限制最多显示500个字，超出范围的时候，截取前面496个字和最后一个字，中间用三个点表示省略
        const keywordContent = keyword.length > MAX_KEYWORD ? `${keyword.slice(0, MAX_KEYWORD - 4)}...${keyword.slice(-1)}` : keyword;
        const replaceWordContent = replaceWord.length > MAX_KEYWORD ? `${replaceWord.slice(0, MAX_KEYWORD - 4)}...${replaceWord.slice(-1)}` : replaceWord;
        this.modal = true;
        Modal.confirm({
            title: i18n.t('提示'),
            content: i18n.t('确定将此文档内的{0}处 “{1}” 全部替换为 “{2}” 吗？', { 0: count, 1: keywordContent, 2: replaceWordContent }),
            onOk: () => {
                this.modal = false;

                tdw({
                    opername: TDWOperationType.DOC_PPT,
                    module: TDWModuleType.PC_SEARCH,
                    action: TDWActionType.PC_SEARCH_REP_ALL_SUC,
                });

                this.exitSearching().then(() => {
                    transaction(() => {
                        this.searchResult.forEach((result: any) => {
                            const { component } = result;
                            if (component instanceof TableDataModel) {
                                // table
                                // TODO 可以改成一次性修改整个表格
                                const fixedResult = this.fixTableResult(result.result);
                                fixedResult.forEach((result) => {
                                    const cellPosition: { row: number; col: number } = result.cell;
                                    const { textPositions } = result;
                                    const tr: any = component.store?.data.graphic?.graphicData?.tbl?.tr;
                                    if (!tr) return;
                                    let { p } = tr[cellPosition.row].tc[cellPosition.col].txBody;

                                    p = this.replacePositionedTextInParagraphList(p, textPositions, keyword, replaceWord);
                                    component.api.transaction(() => {
                                        tr.forEach((tblRow, row) => {
                                            tblRow.tc.forEach((tc, col) => {
                                                if (row === cellPosition.row && col === cellPosition.col) {
                                                    component.api.updateParagraphList(p, col, row);
                                                }
                                            });
                                        });
                                    });
                                });
                            } else {
                                // shape
                                const textPositions = result.result;
                                let paragraphList = this.getParagraph(component);
                                paragraphList = this.replacePositionedTextInParagraphList(paragraphList, textPositions, keyword, replaceWord);
                                component.api.updateParagraphList(paragraphList);
                            }
                        });

                        this.isSearchingFlag = false;

                        this.nextTick(() => {
                            this.search(this.keyword);

                            this.modal = true;
                            Modal.confirm({
                                title: i18n.t('提示'),
                                content: i18n.t('共完成{0}处替换', { 0: count - this.inlineNodeKeywordCount }),
                                onOk: () => {
                                    this.modal = false;
                                },
                                onClose: () => {
                                    this.modal = false;
                                },
                                cancelText: null,
                            });
                            this.inlineNodeKeywordCount = 0; // 每次替换后清除记录的inlineNode内关键词数量
                        });
                    });
                });
            },
            onCancel: () => {
                this.modal = false;
            },
            onClose: () => {
                this.modal = false;
            },
        });
    }

    /**
     * 清除所有高亮的内容
     */
    public clear() {
        // if (!this.isSearchingFlag) return;
        if (this.currentPageResult) {
            this.unMarkResult(this.currentPageResult);
        }
        if (this.lastHighlightPosition) {
            this.cancelHighlight(this.lastHighlightPosition, this.currentPageResult.find(({ component }) => (component === this.lastHighlightPosition.component)));
        }
        this.currentIndex = 0;
        this.keywordCount = 0;
        this.inlineNodeKeywordCount = 0;
        this.keyword = '';
        this.isSearchingFlag = false;
        this.isHighlighting = false;
        this.lastHighlightPosition = null as unknown as Position;
    }

    /**
     * 退出search状态
     * 清除所有高亮
     * 但是keyword，count等继续保留
     */
    public exitSearching(): Promise<any> {
        if (!this.isSearchingFlag) return Promise.resolve();

        const PromiseArr: Array<Promise<any>> = [];
        if (this.currentPageResult) {
            PromiseArr.push(this.unMarkResult(this.currentPageResult));
        }
        if (this.lastHighlightPosition) {
            PromiseArr.push(this.cancelHighlight(this.lastHighlightPosition, this.currentPageResult.find(({ component }) => (component === this.lastHighlightPosition.component))));
        }

        this.isSearchingFlag = false;
        this.isHighlighting = false;
        this.lastHighlightPosition = null as unknown as Position;

        return Promise.all(PromiseArr);
    }

    public fixTableResult(result: Array<{ offset: number; paragraph: number; cell: { row: number; col: number } }>): Array<{ textPositions: Array<{ offset: number; paragraph: number }>; cell: { row: number; col: number } }> {
        return result.reduce((prev, curr) => {
            const fixed = {
                cell: curr.cell,
                textPositions: [{
                    offset: curr.offset,
                    paragraph: curr.paragraph,
                }],
            };

            if (!prev.length) return prev.concat(fixed);

            const last = prev[prev.length - 1];

            if (last.cell.row === fixed.cell.row && last.cell.col === fixed.cell.col) {
                prev[prev.length - 1].textPositions.push(fixed.textPositions[0]);
                return prev;
            }
            return prev.concat(fixed);
        }, [] as Array<{ textPositions: Array<{ offset: number; paragraph: number }>; cell: { row: number; col: number } }>);
    }

    private handleChangeset() {
        if (!this.isSearchingFlag) return;

        const flag = SlideDataServices.SDKDataServices.SearchManager()?.replaceSearchResultFromMutation(this.keyword, this.rawSearchResult);
        const { keyword } = this;
        if (flag) {
            this.updateSearchResult(this.rawSearchResult);
            const newCurrentPageResult = this.getIndexPageResult(
                this.searchResult,
                this.selectionService.currentSlideIndex,
            );
            if (!this.compareResult(this.currentPageResult, newCurrentPageResult)) {
                // 本页数据发生变化了，提示用户重新查找
                this.modal = true;
                Modal.confirm({
                    title: i18n.t('提示'),
                    content: i18n.t('文档内容发生改变，请重新查找'),
                    onOk: () => {
                        this.modal = false;
                        this.clear();
                        this.search(keyword);
                    },
                    onCancel: () => {
                        this.modal = false;
                        this.clear();
                    },
                    onClose: () => {
                        this.modal = false;
                    },
                });
            }
        }
    }

    /**
     * 比较两项结果是否相同
     * @param resultList1
     * @param resultList2
     */
    // eslint-disable-next-line complexity
    private compareResult(resultList1: Array<Result>, resultList2: Array<Result>) {
        if (resultList1.length !== resultList2.length) return false;
        const len = resultList1.length;
        for (let i = 0; i < len; i++) {
            const result1: Result = resultList1[i];
            const result2: Result = resultList2[i];
            if (result1.component !== result2.component) return false;
            if (result1.result.length !== result2.result.length) return false;
            for (let j = 0; j < result1.result.length; j++) {
                const position1 = result1.result[j];
                const position2 = result2.result[j];
                if (position1.offset !== position2.offset) return false;
                if (position1.paragraph !== position2.paragraph) return false;
                if (position1.cell && position2.cell) {
                    if (position1.cell.col !== position2.cell.col) return false;
                    if (position1.cell.row !== position2.cell.row) return false;
                }
            }
        }

        return true;
    }

    private fixSearchResult(searchResult: SearchResult): Array<Result> {
        return searchResult
            // 附件不支持查找替换，搜索结果排除掉附件中的结果
            .filter(({ component }) => !isSdkModelIsAttachment(component))
            .map((result) => {
                const { component } = result;
                let slide = component.getPage();
                if (slide.type === DataType.Notes) {
                    const noteSlide = (slide as INoteSlideDataModel).getSlide();
                    slide = noteSlide || slide;
                }
                const slideIndex = this.selectionService.slides
                    .findIndex(slideModel => slideModel.sdkData === slide);
                return {
                    slideIndex,
                    ...result,
                };
            })
            .sort((a, b) => a.slideIndex - b.slideIndex);
    }

    private updateRawSearchResult(keyword: string) {
        if (keyword === '') {
            this.rawSearchResult = [];
        } else {
            this.rawSearchResult = SlideDataServices.SDKDataServices.SearchManager()?.searchText(keyword) || [];
        }
    }

    private updateSearchResult(rawSearchResult: SearchResult) {
        this.searchResult = this.fixSearchResult(rawSearchResult);
        this.keywordCount = this.searchResult.reduce((prev, { result }) => prev + result.length, 0);
    }

    /**
     * 找到curreentPageIndex页前后的第一个查询结果
     * @param searchResult
     * @param currentPageIndex
     * @param order
     */
    private getFirstCurrentIndex(searchResult: Array<Result>, currentPageIndex: number, order: SearchOrder): number {
        let componentIndex = 0;
        const len = searchResult.length;
        let component: Result;
        let offset = 0; // 偏移值
        if (order === SearchOrder.Last) {
            component = searchResult[len - 1];
            for (componentIndex = len - 1; componentIndex >= 0; componentIndex--) {
                if (searchResult[componentIndex].slideIndex <= currentPageIndex) {
                    component = searchResult[componentIndex];
                    break;
                }
            }
            offset = component.result.length - 1;
        } else {
            [component] = searchResult;
            for (componentIndex = 0; componentIndex < len; componentIndex++) {
                if (searchResult[componentIndex].slideIndex >= currentPageIndex) {
                    component = searchResult[componentIndex];
                    break;
                }
            }
            if (component === searchResult[0]) componentIndex = 0;
        }

        return offset + searchResult.slice(0, componentIndex).reduce((prev, curr) => prev + curr.result.length, 0);
    }

    /**
     * 获取下一个index值
     * @param oldIndex
     * @param order
     * @param length
     * @param initFlag
     */
    private getNextCurrentIndex(oldIndex: number, order: SearchOrder, length: number, initFlag = false): number {
        if (initFlag) return 0;
        let newIndex = oldIndex;

        if (order === SearchOrder.Last) {
            newIndex -= 1;
            (newIndex < 0) && (newIndex = length - 1);
        } else {
            newIndex += 1;
            (newIndex >= length) && (newIndex = 0);
        }

        return newIndex;
    }

    /**
     * 根据resultIndex找到对应的position与pageResult
     * @param searchResult
     * @param currentIndex
     */
    private getResultByIndex(
        searchResult: Array<Result>,
        currentIndex: number,
    ): { position: Position; pageResult: Array<Result> } {
        const position = new Position();

        let index = 0;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < searchResult.length; i++) {
            const result = searchResult[i];
            const len = result.result.length;
            if ((index + len) <= currentIndex) {
                index += len;
                continue;
            }

            position.component = result.component;
            position.pageIndex = result.slideIndex;
            position.offset = currentIndex - index;
            break;
        }

        return {
            position,
            pageResult: this.searchResult.filter(({ slideIndex }) => position.pageIndex === slideIndex),
        };
    }

    /**
     * 跳转到指定页面
     * @param pageIndex 需要跳转到到页面
     */
    private jumpToPage(pageIndex: number): Promise<any> {
        this.changePageFlag = true;
        this.selectionService.changeCurrentSlideIndexByUserAction(pageIndex);
        return Promise.resolve();
    }

    /**
     * 选中result
     * @param result 需要高亮的结果
     */
    private markResult(result: Array<Result>) {
        result.forEach(({
            component,
            result,
        }) => {
            const uiModel: BaseModel|undefined = this.getUIModelBySDKComponent(component);
            if (!uiModel) return;

            if (uiModel instanceof TableModel) {
                // 1. table数据合并 如果后者与前者的cell相等，说明是同一个cell里找到多次，不需要mark多次
                const fixedResult = this.fixTableResult(result as Array<{ offset: number; paragraph: number; cell: { row: number; col: number } }>);

                // 2. 每个进行mark
                fixedResult.forEach(({ cell, textPositions }) => {
                    let model: any = uiModel;
                    model = model.tableItems[cell.row][cell.col];
                    model.editorInstance && model.editorInstance.addSearchMark(textPositions, this.keyword);
                });
            } else {
                const model: any = uiModel;
                model.editorInstance && model.editorInstance.addSearchMark(result, this.keyword);
            }
        });
    }

    private unMarkResult(result: Array<Result>): Promise<any> {
        return Promise.all(result.map(({
            component,
            result,
        }) => {
            const uiModel: BaseModel|undefined = this.getUIModelBySDKComponent(component);
            if (!uiModel) return Promise.resolve();

            if (uiModel instanceof TableModel) {
                const uiTableModel = this.selectionService.currentSlide?.children
                    .find(item => (item.sdkData && item.sdkData === component));
                if (!uiTableModel) return Promise.resolve();

                const fixedResult = this.fixTableResult(result as Array<{ offset: number; paragraph: number; cell: { row: number; col: number } }>);

                return Promise.all(fixedResult.map(({ cell }) => {
                    let model: any = uiModel;
                    model = model.tableItems[cell.row][cell.col];
                    return (model.editorInstance && model.editorInstance.removeSearchMark()) || Promise.resolve();
                }));
            }
            const model: any = uiModel;
            return (model.editorInstance && model.editorInstance.removeSearchMark()) || Promise.resolve();
        }));
    }

    // eslint-disable-next-line complexity
    private highlightPosition(position: Position, result: Result) {
        const { component } = position;
        const uiModel: BaseModel|undefined = this.getUIModelBySDKComponent(component);
        if (!uiModel) return;

        if (uiModel instanceof TableModel) {
            const r = result?.result[position.offset];
            const cell = r?.cell;
            if (!cell) return;

            let model: any = uiModel;
            model = model.tableItems[cell.row][cell.col];
            model.editorInstance && model.editorInstance.addHighlight([r], this.keyword);
        } else {
            const model: any = uiModel;
            model.editorInstance && model.editorInstance.addHighlight([result.result[position.offset]], this.keyword);
        }
    }

    // eslint-disable-next-line complexity
    private cancelHighlight(position: Position, result: Result | undefined) {
        if (!result) return Promise.resolve();
        const { component } = position;
        const uiModel: BaseModel|undefined = this.getUIModelBySDKComponent(component);
        if (!uiModel) return Promise.resolve();

        if (uiModel instanceof TableModel) {
            const r = result?.result[position.offset];
            const cell = r?.cell;
            if (!cell) return Promise.resolve();

            let model: any = uiModel;
            model = model.tableItems[cell.row][cell.col];
            return (model.editorInstance && model.editorInstance.removeHighlight()) || Promise.resolve();
        }
        const model: any = uiModel;
        return (model.editorInstance && model.editorInstance.removeHighlight()) || Promise.resolve();
    }

    private getIndex(position: Position, resultList: Array<Result>): number {
        let index = 0;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < resultList.length; i++) {
            const result = resultList[i];
            if (result.component === position.component) {
                index += position.offset;
                break;
            } else {
                index += result.result.length;
            }
        }

        return index;
    }

    /**
     * 获取特定页面数据
     * @param pageResult
     * @param pageIndex
     */
    private getIndexPageResult(searchResult: Array<Result>, pageIndex: number) {
        return searchResult.filter(({ slideIndex }) => pageIndex === slideIndex);
    }

    /**
     * 更新ui
     * @param currentPageResult 当前页面内容
     */
    private updateSearchResultUI(currentPageResult: Array<Result>) {
        if (this.lastHighlightPosition) {
            this.cancelHighlight(this.lastHighlightPosition, currentPageResult.find(({ component }) => (component === this.lastHighlightPosition.component)));
        }

        this.markResult(currentPageResult);
    }

    private updateHighlightUI(nextResult: { position: Position; pageResult: Array<Result> }) {
        this.lastHighlightPosition = nextResult.position;

        // TODO 可优化
        this.currentIndex = this.getIndex(this.lastHighlightPosition, this.searchResult);

        if (this.currentPageResult !== nextResult.pageResult) {
            this.currentPageResult = nextResult.pageResult;
            this.jumpToPage(this.lastHighlightPosition.pageIndex).then(() => {
                this.changePageFlag = false;
                this.markResult(this.currentPageResult);
                const result = nextResult.pageResult.find(({ component }) => (component === this.lastHighlightPosition.component));
                result && this.highlightPosition(this.lastHighlightPosition, result);
            });
        } else {
            this.markResult(this.currentPageResult);
            const result = nextResult.pageResult.find(({ component }) => (component === this.lastHighlightPosition.component));
            result && this.highlightPosition(this.lastHighlightPosition, result);
        }
    }

    /**
     * 渲染当前的hightlight
     */
    private renderHighlight() {
        mobx.transaction(() => {
            this.isHighlighting = true;

            const nextResult: { position: Position; pageResult: Array<Result> } = this.getResultByIndex(this.searchResult, this.currentIndex);

            if (this.lastHighlightPosition) {
                this.cancelHighlight(this.lastHighlightPosition, this.currentPageResult.find(({ component }) => (component === this.lastHighlightPosition.component)));
            }

            // 如果需要跳页的话，说明currentPageResult需要改变，因此其实mark也要隐式的更新
            if (this.currentPageResult !== nextResult.pageResult) {
                if (this.currentPageResult) {
                    this.unMarkResult(this.currentPageResult);
                }

                this.currentPageResult = nextResult.pageResult;
                this.jumpToPage(nextResult.position.pageIndex).then(() => {
                    this.changePageFlag = false;
                    this.markResult(this.currentPageResult);
                    this.updateHighlightUI(nextResult);
                });
            } else {
                this.updateHighlightUI(nextResult);
            }
        });
    }


    private replacePositionedTextInParagraph(paragraph, offsetList: Array<number>, rawWord: string, replaceWord: string) {
        // 获取textRun中需要更新的部分
        function getReplaceList(
            textRun: { t: string },
            offsetList: Array<number>,
            rawWord: string,
        ): Array<{ start: number; end: number; needReplace: false }> {
            const replaceList: Array<any> = [];
            const end = textRun.t.length;
            const len = rawWord.length;
            offsetList.forEach((offset) => {
                if (offset + len < 0 || offset >= end) {
                    return;
                }

                const raw: any = {
                    needReplace: true,
                };

                if (offset < 0) raw.needReplace = false;
                raw.start = Math.max(0, offset);
                raw.end = Math.min(end, offset + len);

                replaceList.push(raw);
            });

            return replaceList;
        }

        let index = 0;
        let egTextRun = JSON.parse(JSON.stringify(paragraph.egTextRun));

        egTextRun = egTextRun.map((textRun) => {
            const { r } = textRun;
            const replaceList = getReplaceList(r, offsetList.map(offset => offset - index), rawWord);
            if (r?.rPr?.hlinkClick?.iconMode) {
                // icon文字链形式不做转换
                index += r.t.length;
                // 记录本次替换时遇到的inlineNode内关键词数量，替换完成时清除
                this.inlineNodeKeywordCount += replaceList.length;
                return { r };
            }
            index += r.t.length;
            if (replaceList.length) {
                let start = 0;
                let end: number;
                let text = '';
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < replaceList.length; i++) {
                    end = replaceList[i].start;
                    text += r.t.slice(start, end);
                    if (replaceList[i].needReplace) text += replaceWord;
                    start = replaceList[i].end;
                }
                text += r.t.slice(start);
                r.t = text;
            }

            return textRun;
        }).filter(textRun => textRun.r.t);

        return {
            ...paragraph,
            egTextRun,
        };
    }

    private replacePositionedTextInParagraphList(paragraphList: Array<any>, textPositions: Array<{ offset: number; paragraph: number }>, rawWord: string, replaceWord: string) {
        return paragraphList.map((paragraph, index) => this.replacePositionedTextInParagraph(paragraph, textPositions.filter(({ paragraph }) => paragraph === index).map(({ offset }) => offset), rawWord, replaceWord));
    }


    /**
     * 计算raw里有多少个compare字符串
     * @param raw
     */
    private getStringTimes(raw: string, compare: string) {
        let times = -1;
        const { length } = compare;
        if (!length) return 0;
        let offset = -1 * length;
        let rawLowerCase = raw;
        let compareLowerCase = compare;
        // 查找替换时不区分大小写，避免出现A->a替换时，不能连续替换的情况
        if (raw !== compare && raw.toLowerCase() === compare.toLowerCase()) {
            rawLowerCase = raw.toLowerCase();
            compareLowerCase = compare.toLowerCase();
        }

        do {
            times += 1;
            offset = rawLowerCase.indexOf(compareLowerCase, offset + length);
        } while (offset > -1);

        return times;
    }

    // 只针对文本框，表格无缩排逻辑
    private getParagraph(component: ShapeDataModel) {
        const shapeModel = this.selectionService.currentSlide?.children
            .find(element => element.sdkData === component);

        if (!shapeModel) return component.store?.data?.txBody?.p;

        const p = (shapeModel as IShapeModel)?.editorInstance?.getData();
        if (!p) return component.store?.data?.txBody?.p; // 非当前页，采用sdk数据

        // 当前页，采用当前页数据转化，兼容自缩排逻辑
        const sdk = {} as any;
        ShapeModel.convertDataUiToSdk(p, sdk, component);

        return sdk.txBody && (sdk.txBody.p || component.store?.data?.txBody?.p);
    }

    private nextTick(func, ...rest: any) {
        Promise.resolve().then(() => {
            func.apply(this, rest);
        });
    }
}
